/**
 * Dựng chuỗi CSV — phần THUẦN, không đụng DOM, để kiểm thử được.
 *
 * Phần tải file nằm ở apps/web/src/lib/exportCsv.ts.
 */

/**
 * Bọc một ô theo RFC 4180.
 *
 * Hai cái bẫy thật:
 *  - Ô bắt đầu bằng `=`, `+`, `-`, `@` bị Excel hiểu là CÔNG THỨC. Một mô tả
 *    sản phẩm mở đầu bằng dấu trừ, hay một ô do khách hàng nhập, có thể trở
 *    thành lệnh chạy được khi người khác mở file — đây là CSV injection, không
 *    phải chuyện thẩm mỹ. Chèn dấu nháy đơn phía trước để Excel coi là chữ.
 *  - Dấu nháy kép trong nội dung phải nhân đôi, nếu không cả cột lệch.
 */
class CsvDecimal {
  constructor(
    readonly value: number,
    readonly dp: number,
  ) {}
}
/** Explicit finite numeric output, preserving precision without weakening text-cell injection guards. */
export function csvDecimal(value: number, dp = 2): unknown {
  if (!Number.isFinite(value) || !Number.isInteger(dp) || dp < 0 || dp > 8)
    throw new RangeError("Invalid CSV decimal");
  return new CsvDecimal(value, dp);
}

/**
 * Đọc lại số và số lẻ đã gói bằng csvDecimal.
 *
 * Bộ xuất .xlsx cần biết ô này là SỐ để ghi ô số (cộng được, lọc được) thay vì
 * chuỗi "12.50" — lớp CsvDecimal không xuất ra ngoài nên đây là cửa duy nhất.
 */
export function csvDecimalParts(value: unknown): { value: number; dp: number } | null {
  return value instanceof CsvDecimal ? { value: value.value, dp: value.dp } : null;
}

export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (value instanceof CsvDecimal) return value.value.toFixed(value.dp);
  let s = String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => unknown;
}

export function toCsv<T>(columns: CsvColumn<T>[], rows: readonly T[]): string {
  const head = columns.map((c) => csvCell(c.header)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvCell(c.value(r))).join(","))
    .join("\r\n");
  return body ? `${head}\r\n${body}` : head;
}
